import { Fragment, useEffect } from 'react';
import { PageHeader, PageHeaderProps } from 'ui/pageHeader';
import { ColumnData } from 'ui/tableHeader';
import { SimpleTable } from '../SimpleIndexPage/SimpleTable';
import { SimpleIndexContextProvider, useSimpleIndexContext } from './contexts';
import { Row } from './types';
import { LinkContent } from 'ui/pageHeader/types';

type Props = {
  columns: ColumnData[];
  createRowCell: (
    columnValue: string,
  ) => ({ row }: { row: Record<string, string | number> }) => JSX.Element;
  pageTitle: string;
  breadcrumb?: LinkContent;
  projectsBreadcrumb?: {
    content: React.ReactNode | string;
    href: string;
  };
  rows: Row[];
  withHorizontalScroll?: boolean;
  pageActions: PageHeaderProps['pageActions'];
  TableActions?: React.FunctionComponent<React.PropsWithChildren<unknown>>;
  isError?: boolean;
  isLoading: boolean;
  /** Whether it should be possible to filter the columns currently visible. */
  filterableColumns?: boolean;
  /** numLoadingRows defaults to pageSize, but this will override it */
  numLoadingRows?: number;
  queryByFn?: (query: string, row: Row) => boolean;
  /** If pluralResourceName is included, it will be used in the empty state text */
  pluralResourceName?: string;
};

export const SimpleIndex = ({
  rows,
  columns,
  createRowCell,
  pageTitle,
  breadcrumb,
  projectsBreadcrumb,
  isError = false,
  isLoading,
  withHorizontalScroll = false,
  numLoadingRows,
  pageActions,
  TableActions = Fragment,
  filterableColumns,
  pluralResourceName = 'resources',
}: Props) => {
  const { setAllRecords, activeColumns } = useSimpleIndexContext();

  useEffect(() => {
    setAllRecords(rows);
  }, [rows]);

  let rowsColumns = columns.map((column) => ({
    ...column,
    RowCell: createRowCell(column.value),
  })) as ColumnData[];

  if (filterableColumns)
    rowsColumns = rowsColumns.filter((column) => activeColumns.includes(column.value));

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        folderBreadcrumb={projectsBreadcrumb}
        pageActions={pageActions}
      >
        {pageTitle}
      </PageHeader>
      <TableActions />
      <SimpleTable
        rowsColumns={rowsColumns}
        isError={isError}
        isLoading={isLoading}
        withHorizontalScroll={withHorizontalScroll}
        pluralResourceName={pluralResourceName}
        numLoadingRows={numLoadingRows}
      />
    </>
  );
};

export const SimpleIndexPage = (props: Props) => (
  <SimpleIndexContextProvider queryByFn={props.queryByFn}>
    <SimpleIndex {...props} />
  </SimpleIndexContextProvider>
);
